import React, { useCallback, useEffect, useState } from "react";
import { ClipboardList, Search, BrainCircuit, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { api, formatApiError } from "@/lib/api";
import {
  VISIT_TYPES, VISIT_STATUS_COLORS, VISIT_PRIORITY_COLORS, SOURCE_LABELS, timeAgo, READINESS_COLORS,
} from "@/pages/fleet/constants";

export default function FleetVisits() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("__open__");
  const [priorityFilter, setPriorityFilter] = useState("__all__");
  const [sourceFilter, setSourceFilter] = useState("__all__");
  const [recOpen, setRecOpen] = useState(null);
  const [recItems, setRecItems] = useState([]);
  const [recBusy, setRecBusy] = useState(false);
  const [narrative, setNarrative] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 200 };
      if (statusFilter !== "__open__" && statusFilter !== "__all__") params.status = statusFilter;
      if (priorityFilter !== "__all__") params.priority = priorityFilter;
      if (sourceFilter !== "__all__") params.source_module = sourceFilter;
      const r = await api.get("/fleet/visits", { params });
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter, priorityFilter, sourceFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((v) => {
    if (statusFilter === "__open__" && ["completed", "cancelled"].includes(v.status)) return false;
    if (search) {
      const hay = `${v.patient_name || ""} ${v.patient_mrn || ""} ${v.visit_type} ${v.required_skill || ""}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  async function openRecommendations(visit) {
    setRecOpen(visit); setRecBusy(true); setRecItems([]); setNarrative(null);
    try {
      const r = await api.post(`/fleet/visits/${visit.id}/recommend`);
      setRecItems(r.data.recommendations || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecBusy(false); }
  }
  async function fetchNarrative() {
    if (!recOpen) return;
    setRecBusy(true);
    try {
      const r = await api.post(`/fleet/visits/${recOpen.id}/recommend/explain`);
      setNarrative(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecBusy(false); }
  }
  async function assign(clinicianId, ai_accepted) {
    try {
      await api.post(`/fleet/visits/${recOpen.id}/assign`, { clinician_id: clinicianId, ai_accepted });
      toast.success("Visit assigned");
      setRecOpen(null); setRecItems([]); setNarrative(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function statusAction(visit, status, payload = {}) {
    try {
      await api.post(`/fleet/visits/${visit.id}/status`, { status, ...payload });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="fleet-visits-page">
      <PageHeader title="Visit Scheduling Board" subtitle="Visits sourced from Onboarding, RPM, Hotline and manual requests." />
      <div className="hah-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by patient, MRN, visit type…"
            className="bg-transparent outline-none text-sm w-full" data-testid="fleet-visits-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="fleet-visits-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__open__">Open only</SelectItem>
            <SelectItem value="__all__">All</SelectItem>
            {Object.entries(VISIT_STATUS_COLORS).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All priority</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="routine">Routine</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All sources</SelectItem>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} className="border-[#E5E7EB]"><RefreshCw className="w-3.5 h-3.5" /></Button>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-10 text-center text-sm text-[#6B7280]">
          <ClipboardList className="w-8 h-8 mx-auto text-[#94A3B8] mb-2" /> No visits match the current filters.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((v) => {
            const sc = VISIT_STATUS_COLORS[v.status] || {};
            const pc = VISIT_PRIORITY_COLORS[v.priority] || {};
            return (
              <div key={v.id} className="hah-card p-4" data-testid={`visit-row-${v.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: pc.bg, color: pc.text, border: `1px solid ${pc.border}40` }}>
                        {pc.label}
                      </span>
                      <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: sc.bg, color: sc.text }}>
                        {sc.label}
                      </span>
                      <span className="text-[10px] uppercase font-semibold text-[#6B7280]">{SOURCE_LABELS[v.source_module]}</span>
                    </div>
                    <div className="text-sm font-semibold text-[#1F2937]">{VISIT_TYPES[v.visit_type] || v.visit_type}</div>
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      {v.patient_name} <span className="font-mono">({v.patient_mrn})</span>
                      {v.required_skill && <> · needs <strong>{v.required_skill}</strong></>}
                      <> · created {timeAgo(v.created_at)}</>
                    </div>
                    {v.clinician_name && (
                      <div className="text-xs text-[#0B7E8C] mt-0.5">Assigned: {v.clinician_name}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {v.status === "unassigned" && (
                      <Button size="sm" onClick={() => openRecommendations(v)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white text-xs h-7" data-testid={`visit-assign-${v.id}`}>
                        <BrainCircuit className="w-3 h-3 mr-1" /> AI Assign
                      </Button>
                    )}
                    {v.status === "assigned" && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "en_route")} className="border-[#E5E7EB] text-xs h-7">En route</Button>
                    )}
                    {v.status === "en_route" && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "arrived")} className="border-[#E5E7EB] text-xs h-7">Arrived</Button>
                    )}
                    {(v.status === "arrived" || v.status === "assigned") && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "in_progress")} className="border-[#E5E7EB] text-xs h-7">Start</Button>
                    )}
                    {(v.status === "in_progress" || v.status === "arrived") && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "completed")} className="border-[#10B98133] text-[#10B981] text-xs h-7">Complete</Button>
                    )}
                    {!["completed", "cancelled"].includes(v.status) && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "cancelled")} className="border-[#EF444433] text-[#EF4444] text-xs h-7">Cancel</Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AI recommendation dialog */}
      <Dialog open={!!recOpen} onOpenChange={(o) => { if (!o) { setRecOpen(null); setRecItems([]); setNarrative(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>AI Clinician Recommendations</DialogTitle></DialogHeader>
          {recBusy && recItems.length === 0 && <div className="py-4 text-sm text-[#6B7280]">Computing recommendations…</div>}
          {!recBusy && recItems.length === 0 && <div className="py-4 text-sm text-[#94A3B8]">No clinicians available.</div>}
          <div className="space-y-2 max-h-[420px] overflow-auto">
            {recItems.map((r, idx) => (
              <div key={r.clinician_id} className={`rounded-lg border p-3 ${idx === 0 ? "border-[#0B7E8C] bg-[#EAF8FA]" : "border-[#E5E7EB] bg-white"}`}
                data-testid={`fleet-visit-rec-${idx}`}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="text-sm font-semibold text-[#1F2937]">{r.clinician_name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{(r.role_code || "").replace("_", " ")}</div>
                  </div>
                  <div className="text-2xl font-bold text-[#0B7E8C]">{r.score}</div>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-[#475569]">
                  <span>{r.distance_km}km · {r.eta_minutes}min</span>
                  <span>Load {r.workload_pct}%</span>
                  <span style={{ color: (READINESS_COLORS[r.readiness] || {}).text }}>{(READINESS_COLORS[r.readiness] || {}).label}</span>
                  <span>{r.skill_match ? "✓ Skill match" : "✗ Skill mismatch"}</span>
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1">{r.reasons?.join(" · ")}</div>
                <Button size="sm" onClick={() => assign(r.clinician_id, idx === 0)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white text-xs h-7 mt-2"
                  data-testid={`fleet-visit-rec-assign-${idx}`}>Assign {idx === 0 ? "(accept AI)" : ""}</Button>
              </div>
            ))}
          </div>
          {recItems.length > 0 && (
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={fetchNarrative} className="w-full border-[#8B5CF6] text-[#8B5CF6]" data-testid="fleet-visit-rec-explain">
                Why this pick? (LLM)
              </Button>
              {narrative && (
                <div className="mt-2 p-2.5 rounded bg-[#F5F3FF] border border-[#8B5CF6]/30 text-xs text-[#1F2937]">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8B5CF6] mb-1">
                    {narrative.model === "fallback" ? "Deterministic" : narrative.model}
                  </div>
                  {narrative.narrative}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecOpen(null)} className="border-[#E5E7EB]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
